import { motion } from "framer-motion";
import { EVENT, INSTRUCTOR_IMG } from "@/lib/data";
import { maskReveal, fadeUp, easeOutExpo } from "@/lib/motion";
import { track } from "@/lib/track";
import { ArrowDown, ArrowUpRight } from "lucide-react";

/**
 * Signature hero:
 * - Line-by-line masked reveal for the serif headline
 * - Editorial "ledger" numeric card as parallax visual (no stock robots)
 * - Persistent pricing scarcity
 */
export default function Hero() {
  const headlineLines = [
    "Build Agentic AI",
    "Systems for Real",
    <>
      Financial <em className="not-italic tick-underline">Workflows.</em>
    </>,
  ];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-[104px] md:pt-[128px] pb-16 md:pb-24 overflow-hidden"
    >
      {/* Ambient dot grid */}
      <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />

      <div className="relative mx-auto max-w-[1360px] px-5 md:px-8">
        {/* Eyebrow row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.2 }}
          className="flex items-center gap-3 mb-8 md:mb-12"
        >
          <span className="w-2 h-2 rounded-full bg-[color:var(--copper)] animate-pulse" />
          <span
            data-testid="hero-eyebrow"
            className="font-mono text-[11px] md:text-[12px] uppercase tracking-[0.24em] text-[color:var(--ink-2)]"
          >
            {EVENT.eyebrow}
          </span>
          <span className="hidden md:inline h-px w-8 bg-[color:var(--line-strong)]" />
          <span className="hidden md:inline font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--ink-3)]">
            Cohort · Aug 2026
          </span>
        </motion.div>

        {/* Two-column composition */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
          {/* Headline block */}
          <div className="col-span-12 lg:col-span-8">
            <h1
              data-testid="hero-headline"
              className="font-serif text-[42px] sm:text-[62px] md:text-[86px] lg:text-[104px] leading-[0.98] tracking-[-0.035em] text-[color:var(--ink)] font-medium"
            >
              {headlineLines.map((line, i) => (
                <span key={i} className="block reveal-mask">
                  <motion.span
                    className="block"
                    custom={i}
                    initial="hidden"
                    animate="show"
                    variants={maskReveal}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.9,
                ease: easeOutExpo,
                delay: 0.7,
              }}
              data-testid="hero-supporting"
              className="mt-8 max-w-[640px] text-[16px] md:text-[18px] leading-[1.55] text-[color:var(--ink-2)] text-pretty"
            >
              {EVENT.supporting}
            </motion.p>

            {/* CTA row */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.85 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <a
                href="#pricing"
                data-testid="hero-cta-primary"
                onClick={() => track("hero_cta_click", { cta: "view_passes" })}
                className="btn-ink"
              >
                View Certification Passes
                <ArrowDown size={16} strokeWidth={1.8} />
              </a>
              <a
                href="#curriculum"
                data-testid="hero-cta-secondary"
                onClick={() =>
                  track("hero_cta_click", { cta: "explore_curriculum" })
                }
                className="btn-outline"
              >
                Explore the Curriculum
              </a>
            </motion.div>

            {/* Fact grid */}
            <motion.dl
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.08, delayChildren: 1 } },
              }}
              className="mt-14 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6 max-w-[720px]"
            >
              {[
                ["Dates", EVENT.dates],
                ["Time", EVENT.time],
                ["Format", EVENT.format],
                ["Instructor", EVENT.instructor],
              ].map(([k, v]) => (
                <motion.div
                  key={k}
                  variants={fadeUp}
                  className="border-t border-[color:var(--line-strong)] pt-3"
                >
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--ink-3)]">
                    {k}
                  </dt>
                  <dd className="mt-1.5 font-sans text-[14px] md:text-[15px] font-medium text-[color:var(--ink)]">
                    {v}
                  </dd>
                </motion.div>
              ))}
            </motion.dl>
          </div>

          {/* Editorial ledger card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: easeOutExpo, delay: 0.5 }}
            className="col-span-12 lg:col-span-4 relative"
          >
            <LedgerCard />
          </motion.div>
        </div>

        {/* Pricing message bar */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: easeOutExpo, delay: 1.1 }}
          className="mt-14 md:mt-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6 pt-6 border-t border-[color:var(--line-strong)]"
        >
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--ink-3)]">
              Passes available from
            </p>
            <p
              data-testid="hero-lowest-price"
              className="mt-2 font-serif text-[44px] md:text-[56px] leading-none font-medium text-[color:var(--ink)]"
            >
              ${EVENT.lowestPrice}
              <span className="ml-3 font-sans text-[14px] font-normal align-middle text-[color:var(--ink-3)]">
                USD
              </span>
            </p>
          </div>
          <div className="md:text-right">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--copper-2)]">
              VIP Pass · Limited
            </p>
            <p
              data-testid="hero-vip-scarcity"
              className="mt-2 font-serif text-[22px] md:text-[26px] font-medium text-[color:var(--ink)]"
            >
              ${EVENT.vipPrice.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}{" "}
              ·{" "}
              <span className="italic font-normal text-[color:var(--copper-2)]">
                Only {EVENT.vipSeats} available
              </span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function LedgerCard() {
  return (
    <div className="relative">
      {/* Portrait clipped frame */}
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-[color:var(--bone-2)] border border-[color:var(--line-strong)]">
        <img
          src={INSTRUCTOR_IMG}
          alt="Nicole Königstein"
          loading="eager"
          className="w-full h-full object-cover object-top grayscale contrast-[1.05] mix-blend-multiply"
        />
        {/* Green spotlight */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(70% 60% at 50% 92%, rgba(126, 217, 87, 0.32), transparent 62%)",
            mixBlendMode: "multiply",
          }}
        />
        {/* Corner ticks */}
        <TickCorner className="top-2 left-2" />
        <TickCorner className="top-2 right-2 rotate-90" />
        <TickCorner className="bottom-2 left-2 -rotate-90" />
        <TickCorner className="bottom-2 right-2 rotate-180" />

        {/* Overlay caption */}
        <div className="absolute left-3 right-3 bottom-3 flex items-end justify-between text-[color:var(--bone)] mix-blend-difference">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.24em]">
              Instructor · Live
            </p>
            <p className="font-serif text-[18px] leading-tight">
              Nicole Königstein
            </p>
          </div>
          <p className="font-mono text-[10px] tabular-nums opacity-90">
            NK · 26
          </p>
        </div>
      </div>

      {/* Below-card meta strip */}
      <div className="mt-3 grid grid-cols-3 border border-[color:var(--line-strong)] bg-[color:var(--bone-2)]">
        {[
          ["SESSIONS", "04"],
          ["HOURS", "22"],
          ["SEATS · VIP", "10"],
        ].map(([k, v], i) => (
          <div
            key={k}
            className={`px-3 py-3 ${i > 0 ? "border-l border-[color:var(--line-strong)]" : ""}`}
          >
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[color:var(--ink-3)]">
              {k}
            </p>
            <p className="mt-1 font-serif text-[26px] leading-none font-medium tabular-nums text-[color:var(--ink)]">
              {v}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TickCorner({ className = "" }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={`absolute w-4 h-4 stroke-[color:var(--bone)] mix-blend-difference ${className}`}
      fill="none"
      strokeWidth="1"
    >
      <path d="M2 2 L2 8 M2 2 L8 2" />
    </svg>
  );
}
